import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { 
  Plus, Trash2, ArrowLeft, Save,
  CheckCircle2, Loader2, Info
} from 'lucide-react';

// All Indian States with GST State Codes (as per real GST Portal)
const STATES = [
  { code: '01', name: 'Jammu & Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '26', name: 'Dadra & Nagar Haveli and Daman & Diu' },
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman & Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
  { code: '97', name: 'Other Territory' }
];

// Sample GSTINs Master (for practice)
const SAMPLE_GSTINS = {
  '27AABCT1234Q1ZX': { name: 'Tech Solutions India Pvt Ltd', state: '27' },
  '07AABCU9603R1ZM': { name: 'ABC Trading Company', state: '07' },
  '29CQQPS0056J1Z7': { name: 'M/s ThreeDots Creations', state: '29' },
  '09AAICK3547R1ZP': { name: 'Demo Recipient UP Pvt Ltd', state: '09' }
};

// Tax Rates as displayed on real GST Portal (13 fixed rates)
const TAX_RATES = [0, 0.1, 0.25, 1, 1.5, 3, 5, 6, 7.5, 12, 18, 28, 40];

const formatRate = (r) => (Number.isInteger(r) ? `${r}%` : `${r}%`);

const blankItems = () => TAX_RATES.map(rate => ({
  rate,
  taxableValue: '',
  iamt: '',
  camt: '',
  samt: '',
  cess: ''
}));

const blankForm = () => ({
  gstin: '',
  recipientName: '',
  nameInMaster: '',
  invoiceNo: '',
  invoiceDate: '',
  invoiceValue: '',
  pos: '',
  reverseCharge: false,
  intraStateIGST: false,
  invoiceTypeFlag: '', // '' (Regular), 'DE', 'SEWP', 'SEWOP'
  differentialPct: false,
  source: '',
  irn: '',
  irnDate: '',
  items: blankItems()
});

/**
 * B2B Module Flow — Replicates official GST Portal B2B Invoice Entry
 * Reference: return.gst.gov.in/returns/auth/gstr1/b2b
 */
const B2BModuleFlow = ({ records: initialRecords = [], onClose, onUpdateRecords, companyData }) => {
  const [currentStep, setCurrentStep] = useState('list'); // 'list' | 'invoice_form' | 'success'
  const [invoices, setInvoices] = useState(initialRecords);
  const [loading, setLoading] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState(null);
  const [formData, setFormData] = useState(blankForm());
  const [gstinError, setGstinError] = useState('');
  const [gstinNotInMaster, setGstinNotInMaster] = useState(false);

  // Derive supplier state from company GSTIN (first 2 digits)
  const supplierState = useMemo(() => {
    if (companyData?.gstin && companyData.gstin.length >= 2) {
      return companyData.gstin.substring(0, 2);
    }
    try {
      const saved = JSON.parse(localStorage.getItem('practiq_gst_company') || '{}');
      if (saved.gstin && saved.gstin.length >= 2) return saved.gstin.substring(0, 2);
    } catch (e) { /* ignore */ }
    return '07';
  }, [companyData]);

  // Keep invoices synced with parent
  useEffect(() => {
    setInvoices(initialRecords);
  }, [initialRecords]);

  // Compute Supply Type from POS vs Supplier State
  const supplyType = useMemo(() => {
    if (!formData.pos) return '';
    return formData.pos === supplierState ? 'Intra-State' : 'Inter-State';
  }, [formData.pos, supplierState]);

  // Whether IGST applies (Inter-State OR Intra-State w/ IGST override)
  const usesIGST = supplyType === 'Inter-State' || formData.intraStateIGST;

  // Recalculate all item taxes when supply type / IGST flag toggles
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        const taxable = parseFloat(item.taxableValue) || 0;
        const rate = parseFloat(item.rate) || 0;
        if (taxable <= 0 || rate <= 0) {
          return { ...item, iamt: '', camt: '', samt: '' };
        }
        const taxAmt = (taxable * rate) / 100;
        if (usesIGST) {
          return { ...item, iamt: taxAmt.toFixed(2), camt: '', samt: '' };
        }
        const half = (taxAmt / 2).toFixed(2);
        return { ...item, iamt: '', camt: half, samt: half };
      })
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usesIGST]);

  const validateGSTIN = (value) => {
    const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return regex.test(value);
  };

  // Handle GSTIN typing
  const handleGSTINChange = (val) => {
    const upper = val.toUpperCase();
    setGstinError('');
    setGstinNotInMaster(false);

    if (upper.length < 15) {
      setFormData(prev => ({ ...prev, gstin: upper, recipientName: '', nameInMaster: '', pos: '' }));
      return;
    }

    if (!validateGSTIN(upper)) {
      setGstinError('Invalid GSTIN format');
      setFormData(prev => ({ ...prev, gstin: upper }));
      return;
    }

    // Valid GSTIN: lookup in master
    if (SAMPLE_GSTINS[upper]) {
      const m = SAMPLE_GSTINS[upper];
      setFormData(prev => ({
        ...prev,
        gstin: upper,
        recipientName: m.name,
        nameInMaster: m.name,
        pos: m.state
      }));
    } else {
      setGstinNotInMaster(true);
      setFormData(prev => ({
        ...prev,
        gstin: upper,
        recipientName: '',
        nameInMaster: '',
        pos: upper.substring(0, 2)
      }));
    }
  };

  // Update taxable value for a rate (auto-calc IGST or CGST+SGST)
  const updateItemTaxable = (rate, taxableValue) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.rate !== rate) return item;
        const taxable = parseFloat(taxableValue) || 0;
        const rt = parseFloat(rate);
        if (taxable <= 0 || rt <= 0) {
          return { ...item, taxableValue, iamt: '', camt: '', samt: '' };
        }
        const taxAmt = (taxable * rt) / 100;
        if (usesIGST) {
          return { ...item, taxableValue, iamt: taxAmt.toFixed(2), camt: '', samt: '' };
        }
        const half = (taxAmt / 2).toFixed(2);
        return { ...item, taxableValue, iamt: '', camt: half, samt: half };
      })
    }));
  };

  const updateItemCess = (rate, cess) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => item.rate === rate ? { ...item, cess } : item)
    }));
  };

  // Totals (memoized)
  const totals = useMemo(() => {
    return formData.items.reduce((acc, it) => {
      acc.taxable += parseFloat(it.taxableValue) || 0;
      acc.iamt += parseFloat(it.iamt) || 0;
      acc.camt += parseFloat(it.camt) || 0;
      acc.samt += parseFloat(it.samt) || 0;
      acc.cess += parseFloat(it.cess) || 0;
      return acc;
    }, { taxable: 0, iamt: 0, camt: 0, samt: 0, cess: 0 });
  }, [formData.items]);

  const resetForm = () => {
    setFormData(blankForm());
    setGstinError('');
    setGstinNotInMaster(false);
  };

  // Add to master (in-session only)
  const handleAddToMaster = () => {
    if (!formData.recipientName?.trim()) {
      alert('Please enter Recipient Name before adding to master');
      return;
    }
    SAMPLE_GSTINS[formData.gstin] = {
      name: formData.recipientName.trim(),
      state: formData.gstin.substring(0, 2)
    };
    setFormData(prev => ({ ...prev, nameInMaster: prev.recipientName.trim() }));
    setGstinNotInMaster(false);
  };

  const handleSave = () => {
    // Validation
    if (!formData.gstin || !validateGSTIN(formData.gstin)) {
      alert('Please enter a valid 15-character Recipient GSTIN/UIN');
      return;
    }
    if (!formData.recipientName?.trim()) {
      alert('Recipient Name is required');
      return;
    }
    if (!formData.invoiceNo?.trim()) {
      alert('Invoice No is required');
      return;
    }
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!formData.invoiceValue || parseFloat(formData.invoiceValue) <= 0) {
      alert('Total Invoice Value is required');
      return;
    }
    if (!formData.pos) {
      alert('Place of Supply is required');
      return;
    }
    const itemsWithValue = formData.items.filter(it => parseFloat(it.taxableValue) > 0);
    if (itemsWithValue.length === 0) {
      alert('Please enter Taxable Value against at least one tax rate');
      return;
    }

    setLoading(true);
    setTimeout(() => {
      const newInvoice = {
        id: Date.now(),
        gstin: formData.gstin,
        recipientName: formData.recipientName,
        receiverName: formData.recipientName,
        invoiceNo: formData.invoiceNo,
        invoiceDate: formData.invoiceDate,
        invoiceValue: formData.invoiceValue,
        placeOfSupply: formData.pos,
        pos: formData.pos,
        supplyType,
        reverseCharge: formData.reverseCharge ? 'Y' : 'N',
        intraStateIGST: formData.intraStateIGST,
        invoiceType: formData.invoiceTypeFlag || 'R',
        differentialPct: formData.differentialPct,
        items: formData.items.filter(it => parseFloat(it.taxableValue) > 0),
        // Aggregated tax (for list/compat display)
        igst: totals.iamt.toFixed(2),
        cgst: totals.camt.toFixed(2),
        sgst: totals.samt.toFixed(2),
        cess: totals.cess.toFixed(2),
        totals
      };
      const updated = [...invoices, newInvoice];
      setInvoices(updated);
      if (onUpdateRecords) onUpdateRecords(updated);
      setSavedInvoice(newInvoice);
      setCurrentStep('success');
      setLoading(false);
    }, 500);
  };

  const handleDelete = (id) => {
    const updated = invoices.filter(inv => inv.id !== id);
    setInvoices(updated);
    if (onUpdateRecords) onUpdateRecords(updated);
  };

  // Group invoices by Recipient GSTIN (Recipient-wise count view)
  const recipientGroups = useMemo(() => {
    const groups = {};
    invoices.forEach(inv => {
      const k = inv.gstin;
      if (!groups[k]) {
        groups[k] = {
          gstin: k,
          name: inv.recipientName || inv.receiverName || '',
          count: 0,
          totalValue: 0,
          totalTax: 0,
          invoices: []
        };
      }
      groups[k].count += 1;
      groups[k].totalValue += parseFloat(inv.invoiceValue) || 0;
      groups[k].totalTax += (parseFloat(inv.igst) || 0) + (parseFloat(inv.cgst) || 0) + (parseFloat(inv.sgst) || 0);
      groups[k].invoices.push(inv);
    });
    return Object.values(groups);
  }, [invoices]);

  // ============ STEP 1: LIST VIEW (Recipient wise count) ============
  if (currentStep === 'list') {
    return (
      <div className="min-h-[500px] bg-white" data-testid="b2b-list-view">
        {/* Dark Header */}
        <div className="bg-[#1e3a5f] text-white px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="hover:bg-white/10 p-1 rounded" data-testid="b2b-close-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-semibold">4A, 4B, 6B, 6C - B2B, SEZ, DE Invoices</h2>
              <p className="text-sm text-gray-300">Taxable outward supplies to registered persons</p>
            </div>
          </div>
        </div>

        {/* Teal Tab Banner */}
        <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] px-6 pt-3">
          <div className="inline-block bg-white text-[#0d9488] px-5 py-2 rounded-t font-semibold text-sm shadow-sm">
            Recipient wise count
          </div>
        </div>

        {/* Content */}
        <div className="bg-white p-6">
          {recipientGroups.length === 0 ? (
            <div className="bg-[#e8f4f8] border-l-4 border-blue-400 px-4 py-3 mb-6 text-gray-700 text-sm rounded-r" data-testid="b2b-empty-state">
              There are no records to be displayed.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-[#1e3a5f] text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm">#</th>
                    <th className="px-4 py-3 text-left text-sm">Recipient GSTIN</th>
                    <th className="px-4 py-3 text-left text-sm">Recipient Name</th>
                    <th className="px-4 py-3 text-center text-sm">No. of Invoices</th>
                    <th className="px-4 py-3 text-right text-sm">Total Invoice Value (₹)</th>
                    <th className="px-4 py-3 text-right text-sm">Total Tax (₹)</th>
                    <th className="px-4 py-3 text-center text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recipientGroups.map((grp, idx) => (
                    <tr key={grp.gstin} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm font-mono">{grp.gstin}</td>
                      <td className="px-4 py-3 text-sm">{grp.name}</td>
                      <td className="px-4 py-3 text-sm text-center font-semibold text-blue-700">{grp.count}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{grp.totalValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">₹{grp.totalTax.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-1 flex-wrap">
                          {grp.invoices.map(inv => (
                            <button
                              key={inv.id}
                              onClick={() => handleDelete(inv.id)}
                              className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                              title={`Delete invoice ${inv.invoiceNo}`}
                              data-testid={`b2b-delete-${inv.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} className="px-6 border-gray-400" data-testid="b2b-back-btn">
              BACK
            </Button>
            <Button
              onClick={() => { resetForm(); setCurrentStep('invoice_form'); }}
              className="bg-[#1e3a5f] hover:bg-[#2a5298] px-6"
              data-testid="b2b-add-record-btn"
            >
              ADD RECORD
            </Button>
            <Button
              className="bg-[#1e3a5f] hover:bg-[#2a5298] px-6 opacity-60 cursor-not-allowed"
              disabled
              title="EWB import is disabled in simulation mode"
            >
              IMPORT EWB DATA
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ============ STEP 2: ADD INVOICE FORM ============
  if (currentStep === 'invoice_form') {
    return (
      <div className="min-h-[500px] bg-white" data-testid="b2b-invoice-form">
        {/* Dark Header */}
        <div className="bg-[#1e3a5f] text-white px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentStep('list')} className="hover:bg-white/10 p-1 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-lg font-semibold">B2B, SEZ, DE - Add Invoice</h2>
              <p className="text-sm text-gray-300">Enter recipient and invoice details</p>
            </div>
          </div>
        </div>

        {/* Teal Sub-Header */}
        <div className="bg-gradient-to-r from-[#0d9488] to-[#14b8a6] text-white px-6 py-3 flex justify-between items-center">
          <h3 className="font-semibold text-sm">B2B, SEZ, DE - Add Invoice</h3>
          <span className="text-xs flex items-center gap-1.5">
            <span className="w-2 h-2 bg-red-400 rounded-full"></span>
            Indicates Mandatory Fields
          </span>
        </div>

        {/* Scrollable form body */}
        <div className="p-6 overflow-y-auto max-h-[65vh] space-y-5">
          {/* Top Checkboxes (Invoice Type Flags) */}
          <div className="bg-gray-50 border rounded-lg p-4">
            <div className="grid grid-cols-3 gap-x-6 gap-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.invoiceTypeFlag === 'DE'}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, invoiceTypeFlag: c ? 'DE' : '' }))}
                  data-testid="b2b-cb-deemed-exports"
                />
                <span className="text-sm text-gray-700">Deemed Exports</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.invoiceTypeFlag === 'SEWP'}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, invoiceTypeFlag: c ? 'SEWP' : '' }))}
                  data-testid="b2b-cb-sez-with-payment"
                />
                <span className="text-sm text-gray-700">SEZ Supplies with payment</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.invoiceTypeFlag === 'SEWOP'}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, invoiceTypeFlag: c ? 'SEWOP' : '' }))}
                  data-testid="b2b-cb-sez-without-payment"
                />
                <span className="text-sm text-gray-700">SEZ Supplies without payment</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.reverseCharge}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, reverseCharge: !!c }))}
                  data-testid="b2b-cb-reverse-charge"
                />
                <span className="text-sm text-gray-700">Supply attract reverse charge</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.intraStateIGST}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, intraStateIGST: !!c }))}
                  data-testid="b2b-cb-intra-state-igst"
                />
                <span className="text-sm text-gray-700">Intra-State Supplies attracting IGST</span>
              </label>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-200">
              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.differentialPct}
                  onCheckedChange={(c) => setFormData(prev => ({ ...prev, differentialPct: !!c }))}
                  data-testid="b2b-cb-differential-pct"
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-700 leading-snug">
                  Is the supply eligible to be taxed at a differential percentage (%) of the existing rate of tax, as notified by the Government?
                </span>
              </label>
            </div>
          </div>

          {/* Recipient Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-700">
                Recipient GSTIN/UIN<span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.gstin}
                onChange={(e) => handleGSTINChange(e.target.value)}
                placeholder="Search by recipient name as in master or enter GSTIN"
                maxLength={15}
                className={`mt-1 font-mono ${gstinError ? 'border-red-500' : ''}`}
                data-testid="b2b-gstin-input"
              />
              {gstinError && <p className="text-xs text-red-500 mt-1">{gstinError}</p>}
            </div>
            <div>
              <Label className="text-sm text-gray-700">
                Recipient Name<span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.recipientName}
                onChange={(e) => setFormData(prev => ({ ...prev, recipientName: e.target.value }))}
                readOnly={!!formData.nameInMaster}
                className={`mt-1 ${formData.nameInMaster ? 'bg-gray-100' : ''}`}
                data-testid="b2b-recipient-name"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-500">Name as in Master</Label>
              <Input value={formData.nameInMaster} readOnly className="mt-1 bg-gray-100" />
            </div>
          </div>

          {/* GSTIN not in master notice */}
          {gstinNotInMaster && (
            <div className="bg-yellow-50 border border-yellow-300 rounded p-3 flex items-center justify-between gap-3">
              <p className="text-sm text-gray-700">
                GSTIN not present in master/ GSTIN is selected as supplier only.
              </p>
              <Button
                size="sm"
                onClick={handleAddToMaster}
                className="bg-[#1e3a5f] hover:bg-[#2a5298] text-xs"
                data-testid="b2b-add-to-master"
              >
                ADD TO MASTER
              </Button>
            </div>
          )}

          {/* Sample GSTINs hint */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-xs text-blue-900 font-medium mb-1.5">Practice GSTINs (click to use):</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(SAMPLE_GSTINS).map(([g, m]) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => handleGSTINChange(g)}
                  className="text-xs bg-white border border-blue-300 px-2 py-1 rounded font-mono hover:bg-blue-100"
                  data-testid={`b2b-sample-${g}`}
                >
                  {g} <span className="text-gray-500">- {m.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Invoice details Row 1 */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-700">
                Invoice no.<span className="text-red-500">*</span>
              </Label>
              <Input
                value={formData.invoiceNo}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceNo: e.target.value.toUpperCase() }))}
                className="mt-1"
                data-testid="b2b-invoice-no"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-700">
                Invoice date<span className="text-red-500">*</span>
              </Label>
              <Input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                className="mt-1"
                data-testid="b2b-invoice-date"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-700">
                Total invoice value (₹)<span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={formData.invoiceValue}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceValue: e.target.value }))}
                className="mt-1"
                data-testid="b2b-invoice-value"
              />
            </div>
          </div>

          {/* POS / Supply Type Row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-700 flex items-center gap-1">
                POS <Info className="w-3.5 h-3.5 text-gray-400" /><span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.pos}
                onValueChange={(v) => setFormData(prev => ({ ...prev, pos: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="b2b-pos">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {STATES.map(s => (
                    <SelectItem key={s.code} value={s.code}>
                      {s.code}-{s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-500">Supply Type</Label>
              <Input
                value={supplyType}
                readOnly
                className="mt-1 bg-gray-100"
                data-testid="b2b-supply-type"
              />
            </div>
            <div></div>
          </div>

          {/* Source / IRN / IRN date (greyed) */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-gray-500">Source</Label>
              <Input value={formData.source} readOnly className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label className="text-sm text-gray-500">IRN</Label>
              <Input value={formData.irn} readOnly className="mt-1 bg-gray-100" />
            </div>
            <div>
              <Label className="text-sm text-gray-500">IRN date</Label>
              <Input value={formData.irnDate} readOnly className="mt-1 bg-gray-100" />
            </div>
          </div>

          {/* ITEM DETAILS - Fixed-rate Matrix Table */}
          <div>
            <h4 className="text-base font-semibold text-blue-700 mb-3" data-testid="b2b-item-details-heading">
              Item details
            </h4>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 text-center w-20 border-r border-gray-300" rowSpan={2}>Rate (%)</th>
                    <th className="px-3 py-2 text-center border-r border-gray-300" rowSpan={2}>
                      Taxable value (₹)<span className="text-red-500">*</span>
                    </th>
                    <th className="px-3 py-2 text-center border-b border-gray-300" colSpan={usesIGST ? 2 : 3}>
                      Amount of Tax
                    </th>
                  </tr>
                  <tr className="bg-gray-50">
                    {usesIGST ? (
                      <th className="px-3 py-2 text-center border-r border-gray-300">
                        Integrated tax (₹)<span className="text-red-500">*</span>
                      </th>
                    ) : (
                      <>
                        <th className="px-3 py-2 text-center border-r border-gray-300">
                          Central tax (₹)<span className="text-red-500">*</span>
                        </th>
                        <th className="px-3 py-2 text-center border-r border-gray-300">
                          State/UT tax (₹)<span className="text-red-500">*</span>
                        </th>
                      </>
                    )}
                    <th className="px-3 py-2 text-center">Cess (₹)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {formData.items.map((item) => (
                    <tr key={item.rate} className="hover:bg-blue-50/30">
                      <td className="px-3 py-1.5 text-center font-medium border-r text-gray-700">
                        {formatRate(item.rate)}
                      </td>
                      <td className="px-2 py-1.5 border-r">
                        <Input
                          type="number"
                          value={item.taxableValue}
                          onChange={(e) => updateItemTaxable(item.rate, e.target.value)}
                          className="text-right h-8"
                          data-testid={`b2b-taxable-${item.rate}`}
                        />
                      </td>
                      {usesIGST ? (
                        <td className="px-2 py-1.5 border-r">
                          <Input
                            value={item.iamt}
                            readOnly
                            className="text-right h-8 bg-gray-100"
                            data-testid={`b2b-iamt-${item.rate}`}
                          />
                        </td>
                      ) : (
                        <>
                          <td className="px-2 py-1.5 border-r">
                            <Input
                              value={item.camt}
                              readOnly
                              className="text-right h-8 bg-gray-100"
                              data-testid={`b2b-camt-${item.rate}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 border-r">
                            <Input
                              value={item.samt}
                              readOnly
                              className="text-right h-8 bg-gray-100"
                              data-testid={`b2b-samt-${item.rate}`}
                            />
                          </td>
                        </>
                      )}
                      <td className="px-2 py-1.5">
                        <Input
                          type="number"
                          value={item.cess}
                          onChange={(e) => updateItemCess(item.rate, e.target.value)}
                          className="text-right h-8"
                          data-testid={`b2b-cess-${item.rate}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-blue-50 font-semibold text-gray-800 border-t-2 border-blue-300">
                  <tr>
                    <td className="px-3 py-2 text-right border-r">Total</td>
                    <td className="px-3 py-2 text-right border-r" data-testid="b2b-total-taxable">
                      ₹{totals.taxable.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    {usesIGST ? (
                      <td className="px-3 py-2 text-right border-r" data-testid="b2b-total-iamt">
                        ₹{totals.iamt.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-right border-r" data-testid="b2b-total-camt">
                          ₹{totals.camt.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-right border-r" data-testid="b2b-total-samt">
                          ₹{totals.samt.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                      </>
                    )}
                    <td className="px-3 py-2 text-right" data-testid="b2b-total-cess">
                      ₹{totals.cess.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Sticky Footer Buttons */}
        <div className="border-t px-6 py-4 flex justify-end gap-3 bg-gray-50">
          <Button variant="outline" onClick={() => setCurrentStep('list')} className="px-8 border-gray-400" data-testid="b2b-form-back">
            BACK
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#1e3a5f] hover:bg-[#2a5298] px-8"
            data-testid="b2b-save-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            SAVE
          </Button>
        </div>
      </div>
    );
  }

  // ============ STEP 3: SUCCESS ============
  if (currentStep === 'success') {
    return (
      <div className="min-h-[500px] bg-white" data-testid="b2b-success-view">
        <div className="bg-[#1e3a5f] text-white px-6 py-4">
          <h2 className="text-lg font-semibold">B2B Invoice Entry</h2>
          <p className="text-sm text-gray-300">Invoice saved successfully</p>
        </div>
        <div className="p-6">
          <div className="bg-green-100 border border-green-300 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            <span className="text-green-800 font-medium text-lg">Request Accepted Successfully</span>
          </div>

          {savedInvoice && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-gray-700 mb-3">Invoice Added:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">GSTIN:</span> <span className="font-mono">{savedInvoice.gstin}</span></div>
                <div><span className="text-gray-500">Recipient:</span> {savedInvoice.recipientName}</div>
                <div><span className="text-gray-500">Invoice No:</span> {savedInvoice.invoiceNo}</div>
                <div><span className="text-gray-500">Value:</span> ₹{parseFloat(savedInvoice.invoiceValue || 0).toLocaleString('en-IN')}</div>
                <div><span className="text-gray-500">Supply Type:</span> {savedInvoice.supplyType}</div>
                <div><span className="text-gray-500">Total Tax:</span> ₹{(
                  (parseFloat(savedInvoice.igst) || 0) +
                  (parseFloat(savedInvoice.cgst) || 0) +
                  (parseFloat(savedInvoice.sgst) || 0)
                ).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={() => { resetForm(); setCurrentStep('invoice_form'); }}
              className="bg-[#1e3a5f] hover:bg-[#2a5298]"
              data-testid="b2b-add-another"
            >
              <Plus className="w-4 h-4 mr-2" />
              ADD ANOTHER INVOICE
            </Button>
            <Button
              variant="outline"
              onClick={() => { resetForm(); setCurrentStep('list'); }}
              data-testid="b2b-view-all-btn"
            >
              VIEW ALL INVOICES
            </Button>
          </div>
        </div>
        <div className="border-t px-6 py-4 flex justify-end bg-gray-50">
          <Button variant="outline" onClick={() => { resetForm(); setCurrentStep('list'); }}>
            BACK
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default B2BModuleFlow;
